/**
 * Live project channel: one EventSource per open project. Components subscribe to `change` events
 * for the kinds they show; presence lists who is online and on which sheet. After a reconnect the
 * `resync` callback fires so views refetch (anything missed while offline comes back).
 */
import { useEffect, useRef, useState } from "react";
import { actorName, clientId } from "./useDocSync";

export interface LiveChange { kind: "pin" | "sov" | "dailyReport" | "document" | "project"; id: string; action: string; version?: number; by: string; clientId?: string; at: string; conflict?: boolean }
export interface Presence { clientId: string; name: string; documentId?: string; pageId?: string; sheet?: string; since: string }

type Handler = (c: LiveChange) => void;
interface Channel { es: EventSource; handlers: Set<Handler>; resync: Set<() => void>; presence: Presence[]; presenceSubs: Set<(p: Presence[]) => void>; refs: number; status: "connecting" | "live" | "offline"; statusSubs: Set<(s: Channel["status"]) => void>; opened: boolean }
const channels = new Map<string, Channel>();

function open(projectId: string): Channel {
  const existing = channels.get(projectId);
  if (existing) { existing.refs++; return existing; }
  const es = new EventSource(`/api/projects/${projectId}/events?clientId=${encodeURIComponent(clientId())}&name=${encodeURIComponent(actorName())}`);
  const ch: Channel = { es, handlers: new Set(), resync: new Set(), presence: [], presenceSubs: new Set(), refs: 1, status: "connecting", statusSubs: new Set(), opened: false };
  const setStatus = (s: Channel["status"]) => { ch.status = s; ch.statusSubs.forEach((f) => f(s)); };
  es.addEventListener("hello", () => {
    setStatus("live");
    if (ch.opened) ch.resync.forEach((f) => f()); // reconnected: refetch what we show
    ch.opened = true;
    if (lastView.get(projectId)) void postPresence(projectId, lastView.get(projectId)!);
  });
  es.addEventListener("change", (e) => { const c = JSON.parse((e as MessageEvent).data) as LiveChange; ch.handlers.forEach((h) => h(c)); });
  es.addEventListener("presence", (e) => { ch.presence = JSON.parse((e as MessageEvent).data); ch.presenceSubs.forEach((f) => f(ch.presence)); });
  es.onerror = () => setStatus("offline");
  channels.set(projectId, ch);
  return ch;
}
function close(projectId: string) {
  const ch = channels.get(projectId);
  if (!ch || --ch.refs > 0) return;
  ch.es.close(); channels.delete(projectId);
}

const lastView = new Map<string, { documentId?: string; pageId?: string; sheet?: string }>();
async function postPresence(projectId: string, view: { documentId?: string; pageId?: string; sheet?: string }) {
  lastView.set(projectId, view);
  try { await fetch(`/api/projects/${projectId}/presence`, { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ clientId: clientId(), name: actorName(), ...view }) }); } catch { /* offline */ }
}

/** Subscribe to a project's live changes. `onChange` gets other people's changes (and yours when `includeSelf`). */
export function useProjectLive(projectId: string | undefined, onChange: Handler, opts: { kinds?: LiveChange["kind"][]; onResync?: () => void; includeSelf?: boolean; view?: { documentId?: string; pageId?: string; sheet?: string } } = {}) {
  const [presence, setPresence] = useState<Presence[]>([]);
  const [status, setStatus] = useState<Channel["status"]>("connecting");
  const cb = useRef(onChange); cb.current = onChange;
  const rs = useRef(opts.onResync); rs.current = opts.onResync;
  const kinds = (opts.kinds ?? []).join(",");
  useEffect(() => {
    if (!projectId) return;
    const ch = open(projectId);
    const h: Handler = (c) => { if ((!kinds || kinds.split(",").includes(c.kind)) && (opts.includeSelf || c.clientId !== clientId())) cb.current(c); };
    const r = () => rs.current?.();
    ch.handlers.add(h); ch.resync.add(r); ch.presenceSubs.add(setPresence); ch.statusSubs.add(setStatus);
    setPresence(ch.presence); setStatus(ch.status);
    return () => { ch.handlers.delete(h); ch.resync.delete(r); ch.presenceSubs.delete(setPresence); ch.statusSubs.delete(setStatus); close(projectId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, kinds]);
  const viewKey = JSON.stringify(opts.view ?? {});
  useEffect(() => { if (projectId) void postPresence(projectId, opts.view ?? {}); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, viewKey]);
  return { presence, status, me: clientId() };
}

/** Small "Mike changed this" toasts. */
export function useLiveNotices() {
  const [notices, setNotices] = useState<Array<{ id: number; text: string; kind: "info" | "warn" }>>([]);
  const push = (text: string, kind: "info" | "warn" = "info") => {
    const id = Date.now() + Math.random();
    setNotices((n) => [...n.slice(-3), { id, text, kind }]);
    setTimeout(() => setNotices((n) => n.filter((x) => x.id !== id)), kind === "warn" ? 8000 : 4000);
  };
  return { notices, push };
}
